import { motion } from "framer-motion";
import { ArrowDownToLine, ArrowUpRight, MapPin, SquareTerminal } from "lucide-react";
import { MaskLine, EASE } from "@/components/ui/Reveal";
import LifeClock from "@/components/ui/LifeClock";
import Marquee from "@/components/ui/Marquee";
import { MARQUEE_ITEMS, PROFILE } from "@/data/portfolio";
import { useVisitor } from "@/context/VisitorContext";
import { useWindows } from "@/context/WindowContext";
import { downloadResume } from "@/utils/download";

export default function ReadmeWindow() {
  const { open } = useWindows();
  const { name } = useVisitor();

  return (
    <div className="flex min-h-full flex-col p-6 sm:p-8" data-testid="readme-content">
      <div className="flex items-center justify-between gap-3">
        <p className="eyebrow">~/hello — developer workspace</p>
        {name ? (
          <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 font-mono text-[10px] text-slate-300" data-testid="readme-visitor-chip">
            hi, {name}
          </span>
        ) : null}
      </div>

      <h1 className="mt-5 font-display text-[52px] font-extrabold leading-[0.95] tracking-tight sm:text-6xl">
        <MaskLine delay={0.05}>ANISH</MaskLine>
        <MaskLine delay={0.18} className="text-grad-blue">
          RAVI
        </MaskLine>
      </h1>

      <MaskLine delay={0.34}>
        <p className="mt-4 text-base text-slate-200">
          Software Development Engineer — <span className="text-sky-300">Java · Spring Boot · Microservices</span>
        </p>
      </MaskLine>
      <MaskLine delay={0.44}>
        <p className="mt-2 max-w-xl text-sm leading-relaxed text-slate-400">{PROFILE.summary}</p>
      </MaskLine>

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[11px] text-slate-400">
        <span className="flex items-center gap-1.5">
          <MapPin size={12} className="text-sonoma" /> {PROFILE.location}
        </span>
        <span className="flex items-center gap-1.5" data-testid="readme-status">
          <span className="pulse-dot inline-block h-1.5 w-1.5 rounded-full bg-green-400" />
          currently engineering payment infrastructure
        </span>
      </div>

      <div className="mt-5 flex flex-wrap gap-2.5" data-testid="readme-cta-row">
        <motion.button
          whileHover={{ y: -2 }}
          transition={{ duration: 0.18, ease: EASE }}
          onClick={() => open("terminal")}
          data-testid="readme-open-terminal"
          className="flex items-center gap-2 rounded-lg bg-sonoma px-4 py-2 font-mono text-xs font-medium text-white transition-colors duration-150 hover:bg-[#0062CC]"
        >
          <SquareTerminal size={13} /> open terminal
        </motion.button>
        <motion.button
          whileHover={{ y: -2 }}
          transition={{ duration: 0.18, ease: EASE }}
          onClick={() => {
            downloadResume();
          }}
          data-testid="readme-download-resume"
          className="flex items-center gap-2 rounded-lg border border-white/12 bg-white/5 px-4 py-2 font-mono text-xs text-slate-200 transition-colors duration-150 hover:bg-white/10"
        >
          <ArrowDownToLine size={13} /> resume.pdf
        </motion.button>
        <motion.button
          whileHover={{ y: -2 }}
          transition={{ duration: 0.18, ease: EASE }}
          onClick={() => open("contact")}
          data-testid="readme-open-contact"
          className="flex items-center gap-2 rounded-lg border border-white/12 bg-white/5 px-4 py-2 font-mono text-xs text-slate-200 transition-colors duration-150 hover:bg-white/10"
        >
          say hello <ArrowUpRight size={13} />
        </motion.button>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-[1fr_250px]">
        <LifeClock />
        <div className="glass-soft flex flex-col rounded-2xl p-5">
          <p className="eyebrow mb-3">quick commands</p>
          <ul className="space-y-1.5 font-mono text-[11px] text-slate-400">
            <li>
              <span className="text-green-400">➜</span> who is he — the short version
            </li>
            <li>
              <span className="text-green-400">➜</span> skills — full stack tree
            </li>
            <li>
              <span className="text-green-400">➜</span> neofetch — the machine
            </li>
            <li>
              <span className="text-green-400">➜</span> contact — reach me
            </li>
          </ul>
          <button
            onClick={() => open("terminal")}
            data-testid="readme-quick-open-terminal"
            className="mt-auto w-full rounded-lg border border-white/10 bg-white/5 py-2 font-mono text-xs text-slate-200 transition-colors duration-150 hover:bg-white/10"
          >
            open terminal
          </button>
        </div>
      </div>

      <div className="-mx-6 mt-6 sm:-mx-8">
        <Marquee items={MARQUEE_ITEMS} className="border-x-0" />
      </div>
    </div>
  );
}
